/**
 * Feedback moments — US 17.05, the cross-cutting ones. Treatments that
 * belong to a single panel stay there (picture toast in dialogue-panel,
 * death screen in prompts); this module owns the AMBIENT conditions:
 * connection lost/restored and the cutover warning. Audio hooks arrive
 * with US 17.01 (P2).
 */
import { worldBus } from '@shared/bus.js';
import { banner, toast } from './design/components.js';

export function initFeedback(root: HTMLElement): void {
  worldBus.on('notice', ({ text, danger }) => toast(root, text, danger ? 'danger' : 'info'));

  // Connection: tier 1 is deliberately invisible (US 10.02); tier ≥2 shows
  // a persistent banner, and recovery gets a one-off toast.
  let connBanner: { remove: () => void } | null = null;
  worldBus.on('connection-tier', ({ tier }) => {
    const degraded = tier >= 2;
    if (degraded && !connBanner) {
      connBanner = banner(root, '⚠ Connection lost — reconnecting…', 'danger');
    } else if (!degraded && connBanner) {
      connBanner.remove();
      connBanner = null;
      toast(root, 'Connection restored.');
    }
  });

  // Session lock (US 15.05): reuse the hard-freeze treatment — a full
  // overlay this tab never recovers from without a reload.
  worldBus.on('session-locked', () => {
    if (document.querySelector('.wca-lock-overlay')) return;
    const overlay = document.createElement('div');
    overlay.className = 'wca-modal-backdrop wca-lock-overlay';
    const box = document.createElement('div');
    box.className = 'wca-modal';
    box.textContent =
      'Game opened in another window. This tab is paused — close it, or reload to play here instead.';
    overlay.appendChild(box);
    overlay.style.pointerEvents = 'auto';
    root.appendChild(overlay);
  });

  // Cutover warning (US 17.05): the last day deserves a persistent banner,
  // not a HUD footnote someone can miss.
  let cutoverBanner: { remove: () => void } | null = null;
  worldBus.on('city-info', ({ dayInfo }) => {
    const closing = dayInfo.daysUntilCutover !== null && dayInfo.daysUntilCutover <= 1;
    if (closing && !cutoverBanner) {
      cutoverBanner = banner(root, '⏳ The city closes soon — finish what matters!', 'warn');
    } else if (!closing && cutoverBanner) {
      cutoverBanner.remove();
      cutoverBanner = null;
    }
  });
}
